import React, { useState, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import { getActiveApiUrl } from "../services/api";

import SingleColumnResumeTemplate from "./Redesign/SingleColumnResumeTemplate";
import TwoColumnResumeTemplate from "./Redesign/TwoColumnResumeTemplate";

const getResumeApiUrl = () => getActiveApiUrl();

const GRADIENT = "linear-gradient(135deg, #8b5cf6, #ec4899)";

const styles = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(ellipse 900px 600px at 85% 0%, #3d1f78 0%, transparent 60%), radial-gradient(ellipse 800px 700px at 0% 100%, #1e2a6b 0%, transparent 55%), #150a30",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: "28px 24px 70px",
    position: "relative",
    overflow: "hidden",
  },
  blobA: {
    position: "absolute", top: -120, right: -100, width: 480, height: 480, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(236,72,153,0.28), transparent 70%)", filter: "blur(30px)", pointerEvents: "none",
  },
  blobB: {
    position: "absolute", bottom: -160, left: -140, width: 520, height: 520, borderRadius: "50%",
    background: "radial-gradient(circle, rgba(99,102,241,0.28), transparent 70%)", filter: "blur(30px)", pointerEvents: "none",
  },
  container: { maxWidth: 1280, margin: "0 auto", position: "relative", zIndex: 1 },

  nav: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 46, flexWrap: "wrap", gap: 18 },
  navLogo: { display: "flex", alignItems: "center", gap: 10 },
  navLogoIcon: {
    width: 42, height: 42, borderRadius: "50%", background: GRADIENT,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 19, flexShrink: 0,
    boxShadow: "0 6px 16px rgba(139,92,246,0.45)",
  },
  navLogoText: { color: "#fff", fontSize: 13.5, fontWeight: 800, lineHeight: 1.25, letterSpacing: 0.5 },
  navLinks: { display: "flex", gap: 30, alignItems: "center", flexWrap: "wrap" },
  navLink: { color: "rgba(255,255,255,0.72)", fontSize: 14, fontWeight: 600 },
  navCta: {
    background: GRADIENT, color: "#fff", border: "none", borderRadius: 999, padding: "11px 24px",
    fontSize: 13.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6,
    boxShadow: "0 8px 20px rgba(139,92,246,0.4)",
  },

  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 22px",
    background: "rgba(255,255,255,0.06)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.3)",
    borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 26,
  },

  // HERO - CENTER ALIGNED
  hero: { display: "flex", flexDirection: "column", alignItems: "center", gap: 30, marginBottom: 52, textAlign: "center", maxWidth: 720, marginLeft: "auto", marginRight: "auto" },
  heroText: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", color: "#e9d5ff",
    fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
    padding: "7px 18px", borderRadius: 50, marginBottom: 20, border: "1px solid rgba(233,213,255,0.3)",
  },
  title: { fontSize: "clamp(30px, 4vw, 46px)", fontWeight: 800, color: "#fff", margin: 0, lineHeight: 1.12 },
  titleAccent: {
    background: "linear-gradient(90deg, #c084fc, #f472b6)", WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent", backgroundClip: "text",
  },
  subtitle: { color: "rgba(255,255,255,0.62)", fontSize: 15, margin: "18px 0 0", maxWidth: 520, lineHeight: 1.65, textAlign: "center" },
  featureRow: { display: "flex", gap: 26, marginTop: 30, flexWrap: "wrap", justifyContent: "center" },
  featureItem: { display: "flex", alignItems: "center", gap: 10 },
  featureIcon: {
    width: 36, height: 36, borderRadius: 11, background: "rgba(255,255,255,0.08)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
    border: "1px solid rgba(255,255,255,0.12)",
  },
  featureLabel: { color: "#fff", fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 },
  featureSub: { color: "rgba(255,255,255,0.48)", fontSize: 11.5 },

  // MASCOT - CENTER RIGHT
  mascotWrap: { position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 340 },
  mascotFrame: { position: "relative", width: 360, maxWidth: "100%" },
  mascotGlow: {
    position: "absolute", top: "50%", left: "50%", width: 320, height: 320,
    transform: "translate(-50%,-50%)", borderRadius: "50%",
    background: "radial-gradient(circle, rgba(139,92,246,0.4), rgba(236,72,153,0.15) 60%, transparent 75%)",
    filter: "blur(6px)", zIndex: 0,
  },
  mascotImg: {
    width: "100%", height: "auto", borderRadius: 24, objectFit: "contain",
    position: "relative", zIndex: 1,
  },
  floatIcon: (top, side, sideVal, delay) => ({
    position: "absolute", top, [side]: sideVal, width: 44, height: 44, borderRadius: 13,
    background: GRADIENT, display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, boxShadow: "0 10px 26px rgba(139,92,246,0.5)", border: "1px solid rgba(255,255,255,0.25)",
    animation: `rrFloat 4.5s ease-in-out ${delay}s infinite`, zIndex: 2,
  }),

  // PANELS - CENTER ALIGNED
  panelsGrid: (stacked) => ({
    display: "grid", gridTemplateColumns: stacked ? "1fr" : "1.4fr 1fr", gap: 24, alignItems: "start",
    maxWidth: 1100, margin: "0 auto",
  }),
  panel: {
    background: "rgba(255,255,255,0.045)", backdropFilter: "blur(10px)", borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.1)", padding: "26px 26px 28px",
  },
  panelTitle: { fontSize: 15, fontWeight: 700, color: "#fff", margin: "0 0 18px", display: "flex", alignItems: "center" },
  stepTag: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26,
    borderRadius: "50%", background: GRADIENT, color: "#fff", fontSize: 12.5, fontWeight: 700, marginRight: 12, flexShrink: 0,
  },

  dropzone: (active) => ({
    border: `1.5px dashed ${active ? "#c084fc" : "rgba(255,255,255,0.22)"}`, borderRadius: 16, padding: "34px 18px",
    textAlign: "center", cursor: "pointer", background: active ? "rgba(192,132,252,0.08)" : "rgba(255,255,255,0.02)",
    transition: "all 0.25s ease",
  }),
  dropIconWrap: {
    width: 56, height: 56, borderRadius: "50%", background: GRADIENT, margin: "0 auto 14px",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
  },
  dropText: { color: "#fff", fontSize: 14.5, margin: "0 0 4px", fontWeight: 700 },
  dropSub: { color: "rgba(255,255,255,0.45)", fontSize: 12, margin: "0 0 16px" },
  chooseBtn: {
    display: "inline-flex", alignItems: "center", gap: 8, background: GRADIENT, color: "#fff", border: "none",
    padding: "10px 22px", borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
  },
  dropFile: {
    display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(34,197,94,0.12)", color: "#4ade80",
    padding: "7px 16px", borderRadius: 50, fontSize: 12, fontWeight: 600, marginTop: 14,
    border: "1px solid rgba(74,222,128,0.3)",
  },

  photoLabel: {
    fontSize: 15, fontWeight: 700, color: "#fff", margin: "0 0 18px", display: "flex", alignItems: "center", gap: 10,
  },
  photoLabelIcon: {
    width: 26, height: 26, borderRadius: "50%", background: GRADIENT, display: "flex",
    alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0,
  },
  photoDropzone: (active) => ({
    border: `1.5px dashed ${active ? "#c084fc" : "rgba(255,255,255,0.22)"}`, borderRadius: 16, padding: "20px 12px",
    textAlign: "center", cursor: "pointer", background: active ? "rgba(192,132,252,0.08)" : "rgba(255,255,255,0.02)",
    transition: "all 0.25s ease", minHeight: 175, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", boxSizing: "border-box", marginBottom: 22,
  }),
  photoPreviewWrap: { position: "relative", width: 84, height: 84, margin: "0 auto 10px" },
  photoPreviewImg: { width: 84, height: 84, borderRadius: "50%", objectFit: "cover", border: "2px solid #c084fc", display: "block" },
  photoRemoveBtn: {
    position: "absolute", top: -4, right: -4, width: 22, height: 22, borderRadius: "50%",
    background: "#ef4444", color: "#fff", border: "2px solid #150a30", fontSize: 11, fontWeight: 700,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
  },
  photoIconWrap: {
    width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.06)", margin: "0 auto 12px",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, border: "1px solid rgba(255,255,255,0.1)",
  },
  photoText: { color: "#fff", fontSize: 13, margin: "0 0 3px", fontWeight: 700 },
  photoSub: { color: "rgba(255,255,255,0.42)", fontSize: 11, margin: "0 0 14px" },
  uploadPhotoBtn: {
    display: "inline-flex", alignItems: "center", gap: 8, background: GRADIENT, color: "#fff", border: "none",
    padding: "9px 20px", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
  },

  whyBox: {
    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "18px 20px",
  },
  whyTitle: { display: "flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 13.5, fontWeight: 700, margin: "0 0 14px" },
  whyItem: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10, color: "rgba(255,255,255,0.75)", fontSize: 12.5, fontWeight: 500 },
  whyCheck: {
    width: 18, height: 18, borderRadius: "50%", background: "rgba(56,189,248,0.15)", color: "#38bdf8",
    fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "1px solid rgba(56,189,248,0.4)",
  },

  textareaWrap: { position: "relative" },
  textarea: {
    width: "100%", minHeight: 88, borderRadius: 14, border: "1.5px solid rgba(255,255,255,0.14)", padding: "14px 16px 26px",
    fontSize: 13.5, fontFamily: "inherit", color: "#fff", boxSizing: "border-box", resize: "vertical", outline: "none",
    background: "rgba(255,255,255,0.03)",
  },
  charCount: { position: "absolute", right: 14, bottom: 10, fontSize: 11, color: "rgba(255,255,255,0.35)" },
  hint: { fontSize: 12, color: "rgba(255,255,255,0.4)", margin: "8px 0 0" },

  primaryBtn: (disabled) => ({
    display: "block", width: "100%", marginTop: 20, padding: "16px", borderRadius: 999,
    background: disabled ? "rgba(255,255,255,0.08)" : GRADIENT, color: disabled ? "rgba(255,255,255,0.35)" : "#fff",
    border: "none", fontSize: 14.5, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    letterSpacing: 0.2, boxShadow: disabled ? "none" : "0 10px 26px rgba(139,92,246,0.45)",
  }),
  bigCta: (disabled) => ({
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "34px auto 10px",
    padding: "17px 46px", borderRadius: 999, background: disabled ? "rgba(255,255,255,0.08)" : GRADIENT,
    color: disabled ? "rgba(255,255,255,0.35)" : "#fff", border: "none", fontSize: 15.5, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", boxShadow: disabled ? "none" : "0 14px 34px rgba(139,92,246,0.5)",
  }),
  secureText: { textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 12, margin: "0 0 6px" },
  secureAccent: { color: "#c084fc", fontWeight: 600 },

  errorBox: {
    background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 14, padding: "13px 18px",
    color: "#fca5a5", fontSize: 13.5, marginBottom: 22, maxWidth: 1100, marginLeft: "auto", marginRight: "auto",
  },

  spinnerWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "22px 0 4px" },
  spinner: {
    width: 34, height: 34, border: "3px solid rgba(255,255,255,0.12)", borderTop: "3px solid #c084fc",
    borderRadius: "50%", animation: "rdSpin 0.8s linear infinite",
  },
  spinnerText: { color: "rgba(255,255,255,0.6)", fontSize: 12.5, fontWeight: 500 },

  previewPanel: {
    background: "rgba(255,255,255,0.045)", backdropFilter: "blur(10px)", borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.1)", padding: "26px", marginTop: 24, maxWidth: 1100, marginLeft: "auto", marginRight: "auto",
  },
  previewWrap: { overflowX: "auto", background: "rgba(0,0,0,0.2)", borderRadius: 16, padding: 20, marginBottom: 20 },

  actionsRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  downloadBtn: {
    flex: 1, minWidth: 190, padding: "14px", borderRadius: 12, background: "linear-gradient(135deg,#22c55e,#16a34a)",
    color: "#fff", border: "none", fontSize: 14.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 10px 24px rgba(34,197,94,0.35)",
  },
  swapBtn: {
    flex: 1, minWidth: 190, padding: "14px", borderRadius: 12, background: "rgba(255,255,255,0.05)", color: "#fff",
    border: "1.5px solid rgba(255,255,255,0.25)", fontSize: 14.5, fontWeight: 700, cursor: "pointer",
  },
};

export default function ResumeRedesign() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [designComment, setDesignComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [resumeData, setResumeData] = useState(null);
  const [design, setDesign] = useState(null); // AI-generated colors/fonts/spacing
  const [layout, setLayout] = useState("two-column"); // "two-column" | "single"
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(""); // data URL, rendered directly by the templates
  const previewRef = useRef(null);

  const onDrop = useCallback((accepted) => {
    if (accepted?.length > 0) {
      setFile(accepted[0]);
      setError("");
      setResumeData(null);
      setDesign(null);
    }
  }, []);

  const onDropRejected = useCallback(() => {
    setError("Only PDF and DOCX resume files are allowed. Maximum size is 5 MB.");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const onDropPhoto = useCallback((accepted) => {
    const picked = accepted?.[0];
    if (!picked) return;
    setPhotoFile(picked);
    setError("");
    const reader = new FileReader();
    reader.onload = () => setPhotoUrl(reader.result);
    reader.readAsDataURL(picked);
  }, []);

  const onDropPhotoRejected = useCallback(() => {
    setError("Profile photo must be a JPG or PNG image, max 2 MB.");
  }, []);

  const removePhoto = (e) => {
    e.stopPropagation();
    setPhotoFile(null);
    setPhotoUrl("");
  };

  const {
    getRootProps: getPhotoRootProps,
    getInputProps: getPhotoInputProps,
    isDragActive: isPhotoDragActive,
  } = useDropzone({
    onDrop: onDropPhoto,
    onDropRejected: onDropPhotoRejected,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
    maxFiles: 1,
    maxSize: 2 * 1024 * 1024,
  });

  const handleRedesign = async () => {
    if (!file) return setError("Please upload your existing resume first (PDF or DOCX).");

    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("resume", file);
      formData.append("designComment", designComment);

      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "multipart/form-data" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await axios.post(`${getResumeApiUrl()}/resume/redesign`, formData, { headers });
      setResumeData(res.data.resumeData);
      setDesign(res.data.design || null);
      setLayout(res.data.layout || "two-column");
    } catch (err) {
      setError("Could not redesign your resume: " + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!previewRef.current) return;
    setDownloading(true);
    try {
      const node = previewRef.current;
      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width / 2, canvas.height / 2] });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width / 2, canvas.height / 2);
      const name = resumeData?.personalDetails?.firstName
        ? `${resumeData.personalDetails.firstName}_${resumeData.personalDetails.lastName || "Resume"}.pdf`
        : "Redesigned_Resume.pdf";
      pdf.save(name);
    } catch (err) {
      setError("Download failed: " + err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes rdSpin { to { transform: rotate(360deg); } }
        @keyframes rrFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>

      <div style={styles.blobA} aria-hidden="true" />
      <div style={styles.blobB} aria-hidden="true" />

      <div style={styles.container}>
        {/* Decorative top nav */}
        
         

        <button type="button" style={styles.backBtn} onClick={() => navigate("/")}>← Back to Home</button>

        {/* Hero - Center Aligned */}
        <div style={styles.hero}>
          <div style={styles.heroText}>
            <div style={styles.badge}>✨ Redesign My Resume</div>
            <h1 style={styles.title}>
              Upload &amp; Redesign<br /><span style={styles.titleAccent}>Your Resume</span>
            </h1>
            <p style={styles.subtitle}>
              Upload your existing resume, tell us the layout you want, and we'll rebuild it in a fresh design — ready to download.
            </p>

            <div style={styles.featureRow}>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>⚡</div>
                <div>
                  <div style={styles.featureLabel}>AI-Powered Redesign</div>
                  <div style={styles.featureSub}>Smart &amp; Professional</div>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>🛡️</div>
                <div>
                  <div style={styles.featureLabel}>100% Secure</div>
                  <div style={styles.featureSub}>Your data is safe</div>
                </div>
              </div>
              <div style={styles.featureItem}>
                <div style={styles.featureIcon}>⏱️</div>
                <div>
                  <div style={styles.featureLabel}>Super Fast Delivery</div>
                  <div style={styles.featureSub}>Get it in minutes</div>
                </div>
              </div>
            </div>
          </div>

       
        </div>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        {/* Step 1 + 2 / Photo panel - Center Aligned */}
        <div style={styles.panelsGrid(!!resumeData)}>
          <div style={styles.panel}>
            <div style={styles.panelTitle}><span style={styles.stepTag}>1</span>Upload your existing resume</div>
            <div {...getRootProps()} style={styles.dropzone(isDragActive)}>
              <input {...getInputProps()} />
              <div style={styles.dropIconWrap}>{isDragActive ? "📂" : "📄"}</div>
              <p style={styles.dropText}>{isDragActive ? "Drop your resume here…" : "Drag & drop or click to select"}</p>
              <p style={styles.dropSub}>Supported: PDF, DOCX · Max size: 5 MB</p>
              <button type="button" style={styles.chooseBtn}>⬆️ Choose File</button>
              {file && <div style={styles.dropFile}>✅ {file.name}</div>}
            </div>

            <div style={{ ...styles.panelTitle, marginTop: 28 }}><span style={styles.stepTag}>2</span>Describe the design you want</div>
            <div style={styles.textareaWrap}>
              <textarea
                style={styles.textarea}
                placeholder='e.g. "Bold, modern, navy and gold, single page" or "Minimal elegant serif look with a soft sidebar"'
                value={designComment}
                onChange={(e) => setDesignComment(e.target.value)}
                maxLength={500}
              />
              <span style={styles.charCount}>{designComment.length}/500</span>
            </div>
            <p style={styles.hint}>Describe colors, mood, fonts, or layout in your own words — the AI will design it to match.</p>

            <button type="button" style={styles.primaryBtn(!file || loading)} disabled={!file || loading} onClick={handleRedesign}>
              {loading ? "⏳ Redesigning your resume…" : "✨ Redesign My Resume"}
            </button>

            {loading && (
              <div style={styles.spinnerWrap}>
                <div style={styles.spinner} />
                <span style={styles.spinnerText}>Reading your resume and rebuilding the design…</span>
              </div>
            )}
          </div>

          <div style={styles.panel}>
            <div style={styles.photoLabel}><span style={styles.photoLabelIcon}>👤</span>Profile photo (optional)</div>
            <div {...getPhotoRootProps()} style={styles.photoDropzone(isPhotoDragActive)}>
              <input {...getPhotoInputProps()} />
              {photoUrl ? (
                <div style={styles.photoPreviewWrap}>
                  <img src={photoUrl} alt="Profile preview" style={styles.photoPreviewImg} />
                  <button type="button" style={styles.photoRemoveBtn} onClick={removePhoto} title="Remove photo">✕</button>
                </div>
              ) : (
                <>
                  <div style={styles.photoIconWrap}>{isPhotoDragActive ? "📂" : "🖼️"}</div>
                  <p style={styles.photoText}>{isPhotoDragActive ? "Drop photo…" : "Add photo"}</p>
                  <p style={styles.photoSub}>JPG/PNG · Max 2 MB</p>
                  <button type="button" style={styles.uploadPhotoBtn}>⬆️ Upload Photo</button>
                </>
              )}
            </div>

            <div style={styles.whyBox}>
              <div style={styles.whyTitle}>✨ Why choose us?</div>
              {["Modern & ATS-Friendly Designs", "Expert Designers", "Unlimited Revisions", "24/7 Customer Support"].map((t) => (
                <div key={t} style={styles.whyItem}>
                  <span style={styles.whyCheck}>✓</span>{t}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Big CTA (mirrors the panel button, decorative — triggers the same action) */}
        <button type="button" style={styles.bigCta(!file || loading)} disabled={!file || loading} onClick={handleRedesign}>
          {loading ? "⏳ Redesigning your resume…" : "✨ Redesign My Resume"}
        </button>
        <p style={styles.secureText}>🔒 Your information is <span style={styles.secureAccent}>confidential and secure</span>.</p>

        {/* Step 3: Preview + download */}
        {resumeData && (
          <div style={styles.previewPanel}>
            <div style={styles.panelTitle}><span style={styles.stepTag}>3</span>Your redesigned resume</div>

            <div style={styles.previewWrap}>
              <div ref={previewRef}>
                {layout === "single" ? (
                  <SingleColumnResumeTemplate data={resumeData} design={design} photoUrl={photoUrl} />
                ) : (
                  <TwoColumnResumeTemplate data={resumeData} design={design} photoUrl={photoUrl} />
                )}
              </div>
            </div>

            <div style={styles.actionsRow}>
              <button type="button" style={styles.downloadBtn} onClick={handleDownload} disabled={downloading}>
                {downloading ? "⏳ Preparing PDF…" : "⬇️ Download as PDF"}
              </button>
              <button
                type="button"
                style={styles.swapBtn}
                onClick={() => setLayout((l) => (l === "single" ? "two-column" : "single"))}
              >
                🔄 Switch to {layout === "single" ? "2-Column" : "Single Page"} Layout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}